import { reportStepsAPI } from '../../services/api';
import { useReportState } from './useReportState';
import { ReportStep } from './types';
import { getTaskSelection } from '../../utils/taskIdValidator';
import i18n from '../../i18n';

export const useCustomReport = () => {
  const {
    state,
    setTaskId,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    setIsLoading,
    setError,
    setReports,
    setSelectedReportIndices,
    setAnalyticsData,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    updateAnalyticsState
  } = useReportState();
  
  // Функция для добавления плейсхолдера отчета
  const addReportPlaceholder = (query: string): number => {
    // Получаем текущие отчеты
    const currentReports = [...state.reports];
    
    // Создаем новый индекс
    const newIndex = currentReports.length;
    
    // Создаем плейсхолдер отчета
    const placeholderReport: ReportStep = {
      name: `Пользовательский отчет (загрузка...)`,
      status: 'processing',
      index: newIndex,
      query_suggestion: query,
      sheets: [],
      processing_steps: [],
      error: null,
      deleted: false,
      isPlaceholder: true
    };
    
    // Добавляем плейсхолдер в список отчетов
    setReports([...currentReports, placeholderReport]);
    
    return newIndex;
  };

  // Генерация client_id для SSE соединения
  const generateClientId = () => {
    // Try to get the user ID from the token
    let userId = "anonymous";
    const token = localStorage.getItem('token');
    
    if (token) {
      try {
        // JWT tokens are in the format header.payload.signature
        // We need to decode the payload (second part)
        const payload = token.split('.')[1];
        // Decode the base64 payload
        const decodedPayload = JSON.parse(atob(payload));
        // Get the user ID from the sub claim
        if (decodedPayload.sub) {
          userId = decodedPayload.sub;
        }
      } catch (error) {
        console.error('Error decoding token:', error);
      }
    }
    
    return `user_${userId}_${Math.random().toString(36).substring(2, 9)}`;
  };

  const generateCustomReport = async (query: string, existingClientId?: string) => {
    const { taskId: currentTaskId } = getTaskSelection();

    if (!currentTaskId) {
      setError(i18n.t('create:processing.error_task_not_init'));
      return;
    }

    // Use existing SSE client_id if provided, otherwise generate a new one
    const clientId = existingClientId || generateClientId();
    console.log('[CUSTOM_REPORT] generateCustomReport - используем client_id для SSE:', clientId, existingClientId ? '(existing)' : '(new)');
    
    try {
      // Добавляем плейсхолдер и получаем его индекс
      const placeholderIndex = addReportPlaceholder(query);
      
      // Не устанавливаем глобальный isLoading, чтобы не перерисовывать все карточки
      setError(null);
      
      // Update taskId from localStorage if needed
      if (currentTaskId !== state.taskId) {
        console.log('generateCustomReport - обновляем taskId из localStorage:', currentTaskId);
        setTaskId(currentTaskId);
      }
      
      // Call API to generate custom report
      const response = await reportStepsAPI.generateCustomReport(currentTaskId, query, clientId);
      
      // Update analytics data if present in response
      if (response?.data?.analytics_data) {
        console.log('generateCustomReport - получены обновленные аналитические данные:', response.data.analytics_data);
        
        if (response.data.analytics_data.reports && Array.isArray(response.data.analytics_data.reports)) {
          // Находим новый отчет в ответе API
          const newReportIndex = response.data.new_report_index;
          const newReportData = response.data.analytics_data.reports.find(
            (r: any) => r.index === newReportIndex - 1 || r.index === newReportIndex
          );
          
          if (newReportData) {
            // Получаем текущие отчеты
            const updatedReports = [...state.reports];
            
            // Заменяем плейсхолдер на реальные данные
            updatedReports[placeholderIndex] = {
              name: newReportData.name,
              status: newReportData.status || 'pending',
              index: newReportIndex,
              sheets: newReportData.sheets || [],
              processing_steps: newReportData.processing_steps || [],
              query_suggestion: newReportData.query_suggestion || query,
              error: newReportData.error || null,
              deleted: false,
              isPlaceholder: false
            };
            
            // Обновляем только список отчетов
            setReports(updatedReports);
            
            // Добавляем новый отчет в выбранные
            const currentIndices = state.selectedReportIndices || [];
            if (!currentIndices.includes(newReportIndex)) {
              setSelectedReportIndices([...currentIndices, newReportIndex]);
            }
            
            // Обновляем аналитические данные без перерисовки отчетов
            if (state.analyticsData) {
              const updatedAnalyticsData = { ...state.analyticsData };
              if (updatedAnalyticsData.reports) {
                updatedAnalyticsData.reports = response.data.analytics_data.reports;
              }
              setAnalyticsData(updatedAnalyticsData);
            }
          }
        }
      }
      
    } catch (err: any) {
      // В случае ошибки обновляем плейсхолдер
      const updatedReports = [...state.reports];
      if (updatedReports.length > 0) {
        const placeholderIndex = updatedReports.findIndex(r => r.isPlaceholder);
        if (placeholderIndex !== -1) {
          updatedReports[placeholderIndex] = {
            ...updatedReports[placeholderIndex],
            status: 'error',
            error: err.response?.data?.detail || i18n.t('common:error'),
            isPlaceholder: false
          };
          setReports(updatedReports);
        }
      }
      setError(err.response?.data?.detail || i18n.t('common:error'));
    }
  };

  return {
    generateCustomReport,
    isLoading: state.isLoading,
    error: state.error,
    analyticsData: state.analyticsData,
    reports: state.reports
  };
};
